import { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { supabase } from "./lib/supabase";
import { invalidateCache } from "./lib/cache";
import { S, brandColor, brandFromSlug, timeAgo, t } from "./data/constants";
import { Logo, Avatar } from "./components/UI";
import { isAdmin } from "./admin/isAdmin";
import { Footer } from "./components/Footer";
import { FeedbackButton } from "./components/FeedbackButton";
import { useIsMobile } from "./hooks/useIsMobile";

// Páginas: se cargan solo cuando se visitan en vez de todas de golpe al
// arrancar la app (React.lazy + <Suspense> más abajo).
const FeedPage = lazy(()=>import("./pages/FeedPage").then(m=>({default:m.FeedPage})));
const GaragePage = lazy(()=>import("./pages/GaragePage").then(m=>({default:m.GaragePage})));
const WristCheckPage = lazy(()=>import("./pages/WristCheckPage").then(m=>({default:m.WristCheckPage})));
const MantenimientoPage = lazy(()=>import("./pages/MantenimientoPage").then(m=>({default:m.MantenimientoPage})));
const ListasPage = lazy(()=>import("./pages/ListasPage").then(m=>({default:m.ListasPage})));
const WorkshopsPage = lazy(()=>import("./pages/WorkshopsPage").then(m=>({default:m.WorkshopsPage})));
const RankingPage = lazy(()=>import("./pages/RankingPage").then(m=>({default:m.RankingPage})));
const OnboardingPage = lazy(()=>import("./pages/OnboardingPage").then(m=>({default:m.OnboardingPage})));
const OnboardingTaller = lazy(()=>import("./pages/OnboardingTaller").then(m=>({default:m.OnboardingTaller})));
const OnboardingMarca = lazy(()=>import("./pages/OnboardingMarca").then(m=>({default:m.OnboardingMarca})));
const CreateWatchPage = lazy(()=>import("./pages/CreateWatchPage").then(m=>({default:m.CreateWatchPage})));
const ExplorePage = lazy(()=>import("./pages/ExplorePage").then(m=>({default:m.ExplorePage})));
const RelojesPage = lazy(()=>import("./pages/RelojesPage").then(m=>({default:m.RelojesPage})));
const ForosPage = lazy(()=>import("./pages/ForosPage").then(m=>({default:m.ForosPage})));
const WatchPage = lazy(()=>import("./pages/WatchPage").then(m=>({default:m.WatchPage})));
const BrandPage = lazy(()=>import("./pages/BrandPage").then(m=>({default:m.BrandPage})));
const ThreadPage = lazy(()=>import("./pages/ThreadPage").then(m=>({default:m.ThreadPage})));
const ProfilePage = lazy(()=>import("./pages/ProfilePage").then(m=>({default:m.ProfilePage})));
const SettingsPage = lazy(()=>import("./pages/SettingsPage").then(m=>({default:m.SettingsPage})));
const AuthPage = lazy(()=>import("./pages/AuthPage").then(m=>({default:m.AuthPage})));
const NotificationsPanel = lazy(()=>import("./pages/NotificationsPanel").then(m=>({default:m.NotificationsPanel})));
const AdminPage = lazy(()=>import("./admin/AdminPage").then(m=>({default:m.AdminPage})));
const PrivacyPage = lazy(()=>import("./pages/PrivacyPage").then(m=>({default:m.PrivacyPage})));
const TermsPage = lazy(()=>import("./pages/TermsPage").then(m=>({default:m.TermsPage})));

function PageLoader() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 0" }}>
      <Logo height={40} />
    </div>
  );
}

// Traduce los mismos nombres de "página" que ya usaban las páginas via
// onNavigate(name, id) a una URL real, para no tener que tocar cada page.
function pageToPath(name, id) {
  switch (name) {
    case "feed": return "/";
    case "explore": return "/explore";
    case "relojes": return "/relojes";
    case "foros": return "/foros";
    case "watch": return `/watch/${id}`;
    case "brand": return `/brand/${id}`;
    case "thread": return `/thread/${id}`;
    case "profile": return `/profile/${id}`;
    case "settings": return "/settings";
    case "garage": return "/garage";
    case "mantenimiento": return "/mantenimiento";
    case "garage_watch": return `/garage/${id}`;
    case "wristcheck": return "/wristcheck";
    case "listas": return "/listas";
    case "talleres": return "/talleres";
    case "ranking": return "/ranking";
    case "create-watch": return "/create-watch";
    case "admin": return "/admin";
    case "privacidad": return "/privacidad";
    case "terminos": return "/terminos";
    default: return "/";
  }
}

function WatchRoute(props) {
  const { slug } = useParams();
  return <WatchPage slug={slug} {...props} />;
}

function BrandRoute(props) {
  const { brandSlug } = useParams();
  return <BrandPage brandSlug={brandSlug} {...props} />;
}

function ThreadRoute(props) {
  const { threadId } = useParams();
  return <ThreadPage threadId={threadId} {...props} />;
}

function ProfileRoute(props) {
  const { userId } = useParams();
  return <ProfilePage userId={userId} {...props} />;
}

function GarageWatchRoute(props) {
  const { watchId } = useParams();
  return <GaragePage openWatchId={watchId} {...props} />;
}

export default function App() {
  return (
    <BrowserRouter>
      {/* Fuera de AppInner a propósito: AppInner hace varios `return` tempranos
          (pantalla de carga, AuthPage, onboarding) y SpeedInsights estaba solo
          en el último, así que la landing — la página que más visitas recibe —
          nunca reportaba nada y Vercel mostraba "No data available". */}
      <SpeedInsights />
      <AppInner />
    </BrowserRouter>
  );
}

function AppInner() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [guestMode, setGuestMode] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [lang, setLang] = useState(localStorage.getItem("ww_lang")||"es");
  const toggleLang = () => { const nl = lang==="es"?"en":"es"; setLang(nl); localStorage.setItem("ww_lang",nl); };

  const routerNavigate = useNavigate();
  const location = useLocation();

  useEffect(()=>{ window.scrollTo(0,0); },[location.pathname]);
  // Al navegar se cierra el desplegable; si no, queda abierto sobre la página nueva.
  useEffect(()=>{ setMenuOpen(false); },[location.pathname]);

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setSession(session);
      if(session){ loadProfile(session.user.id); loadUnread(session.user.id); }
      setAuthChecked(true);
      cleanAuthRedirectUrl();
    });
    // INITIAL_SESSION duplica lo que getSession() ya acaba de resolver arriba —
    // solo nos interesan los cambios reales (login, logout, refresh) que llegan después.
    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      if(event==="INITIAL_SESSION") return;
      // Al entrar o salir cambia quién es el usuario, y con él lo que puede
      // ver: la caché de páginas del anterior no vale. TOKEN_REFRESHED no
      // entra aquí a propósito — pasa cada rato y vaciaría la caché sin que
      // haya cambiado nada.
      if(event==="SIGNED_IN"||event==="SIGNED_OUT") invalidateCache();
      setSession(session);
      if(session){ loadProfile(session.user.id); loadUnread(session.user.id); }
      else { setProfile(null); setProfileMissing(false); }
      cleanAuthRedirectUrl();
    });
    return ()=>subscription.unsubscribe();
  },[]);

  // El callback de OAuth (Google) vuelve con #access_token=... o ?code=...
  // en la URL. Supabase ya lo procesó en este punto; si no lo limpiamos,
  // el navegador se queda con un "#" o "?code=" colgando en cada página.
  function cleanAuthRedirectUrl() {
    if (window.location.hash.includes("access_token") || window.location.search.includes("code=")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }

  async function loadProfile(uid) {
    // maybeSingle: con .single() un perfil inexistente devuelve error y el
    // profile se quedaba en null sin que nadie se enterase.
    // Por RPC y no con select("*"): las columnas privadas de profiles
    // (is_admin, datos demográficos) ya no son legibles por el rol
    // authenticated, y aquí hace falta is_admin para decidir si se muestra
    // el panel. get_my_profile devuelve el perfil completo del que llama.
    const {data,error}=await supabase.rpc("get_my_profile");
    // Sólo "no existe" cuando la consulta fue bien y no hay fila. Si falla por
    // red o 5xx no marcamos nada: esto se reejecuta en cada TOKEN_REFRESHED y
    // un fallo pasajero echaría a la calle a una sesión perfectamente válida.
    if(error) return;
    setProfile(data);
    setProfileMissing(!data);
  }

  async function loadUnread(uid) {
    try {
      const {count}=await supabase.from("notifications").select("*",{count:"exact",head:true}).eq("recipient_id",uid).eq("read",false);
      setUnreadCount(count||0);
    } catch(e) { setUnreadCount(0); }
  }

  const navigate=(name,id=null)=>{ setShowNotifs(false); setShowUserMenu(false); updateOG(name,id); routerNavigate(pageToPath(name,id)); };

  function updateOG(pageName, id) {
    // El dominio se lee del navegador en vez de estar escrito a mano: así los
    // og:url siguen siendo correctos en producción, en las previews de Vercel
    // y en local, y un cambio de dominio no obliga a tocar código.
    const base = window.location.origin;
    const metas = {
      feed: { title:"Wwatch — Feed", desc:"La red social de los amantes de los relojes", url:base },
      explore: { title:"Explorar — Wwatch", desc:"Descubre relojes, coleccionistas y marcas", url:`${base}/explore` },
      foros: { title:"Foros — Wwatch", desc:"Debates sobre relojería con la comunidad", url:`${base}/foros` },
      relojes: { title:"Relojes — Wwatch", desc:"El catálogo de relojes más completo", url:`${base}/relojes` },
      watch: { title:"Wwatch — Reloj", desc:"Ficha completa del reloj", url:`${base}/watch/${id}` },
      brand: { title:`${id} — Wwatch`, desc:`Todos los relojes de ${id}`, url:`${base}/brand/${id}` },
      profile: { title:"Perfil — Wwatch", desc:"Perfil de coleccionista", url:`${base}/profile/${id}` },
    };
    const m = metas[pageName]||metas.feed;
    document.title = m.title;
    ["og:title","twitter:title"].forEach(p=>{ let el=document.querySelector(`meta[property="${p}"]`)||document.createElement("meta"); el.setAttribute("property",p); el.setAttribute("content",m.title); document.head.appendChild(el); });
    ["og:description","twitter:description"].forEach(p=>{ let el=document.querySelector(`meta[property="${p}"]`)||document.createElement("meta"); el.setAttribute("property",p); el.setAttribute("content",m.desc); document.head.appendChild(el); });
    ["og:url","twitter:url"].forEach(p=>{ let el=document.querySelector(`meta[property="${p}"]`)||document.createElement("meta"); el.setAttribute("property",p); el.setAttribute("content",m.url); document.head.appendChild(el); });
  }
  async function signOut() {
    await supabase.auth.signOut();
    // La caché de páginas guarda filas leídas con los permisos del usuario
    // que se va (su feed, sus likes). Si no se vacía, el siguiente que entre
    // en este navegador vería ese contenido hasta que caduque.
    invalidateCache();
    setSession(null); setProfile(null); setProfileMissing(false); setGuestMode(false); routerNavigate("/explore");
  }

  if(!authChecked) return (
    <div style={{ ...S.app, display:"flex", alignItems:"center", justifyContent:"center", background:"#fff" }}>
      <Logo height={60} />
    </div>
  );

  if(!session && !guestMode) return <Suspense fallback={<PageLoader />}><AuthPage onExplore={()=>{ setGuestMode(true); routerNavigate("/explore"); }} /></Suspense>;

  // Sesión válida pero sin fila en profiles: pasa al volver a entrar en una
  // cuenta borrada (borrar cuenta elimina el perfil pero no el usuario de
  // auth). Antes se colaba al render normal con profile=null y la app se
  // quedaba a medias sin decir nada.
  if(session && profileMissing) return (
    <div style={{ ...S.app, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#fff", gap:16, padding:24, textAlign:"center" }}>
      <Logo height={48} />
      <h2 style={{ ...S.h1, marginBottom:0 }}>No encontramos tu perfil</h2>
      <p style={{ ...S.muted, maxWidth:360 }}>Esta cuenta ya no tiene un perfil asociado. Si la borraste, vuelve a registrarte con otro correo.</p>
      <button style={S.btn("primary")} onClick={signOut}>Cerrar sesión</button>
    </div>
  );

  if(session && profile && !profile.onboarding_complete) {
    if(profile.account_type==="repairer") return <Suspense fallback={<PageLoader />}><OnboardingTaller user={session.user} onComplete={()=>{ loadProfile(session.user.id); routerNavigate("/"); }} /></Suspense>;
    if(profile.account_type==="brand") return <Suspense fallback={<PageLoader />}><OnboardingMarca user={session.user} onComplete={()=>{ loadProfile(session.user.id); routerNavigate("/"); }} /></Suspense>;
    // onAccountTypeChanged sólo recarga el perfil, sin navegar: al cambiar
    // account_type este mismo bloque vuelve a evaluarse y monta el onboarding
    // de taller o de marca en lugar del de coleccionista.
    return <Suspense fallback={<PageLoader />}><OnboardingPage user={session.user} onComplete={()=>{ loadProfile(session.user.id); routerNavigate("/"); }} onAccountTypeChanged={()=>loadProfile(session.user.id)} /></Suspense>;
  }

  const currentUser = session ? session.user : null;
  const NAV = session
    ? [{id:"feed",label:t(lang,"feed")},{id:"explore",label:t(lang,"explore")},{id:"foros",label:t(lang,"forums")},{id:"miperfil",label:t(lang,"profile")}]
    : [{id:"explore",label:"Explorar"},{id:"foros",label:"Foros"}];

  return (
    <div style={S.app}>
      {/* position:"relative" pisaba el position:"sticky" de S.nav (misma
          clave, gana el último valor) y anulaba el sticky del todo. No hacía
          falta: sticky ya sirve de referencia para los desplegables en
          position:absolute de dentro, igual que relative. */}
      <nav style={S.nav}>
        <div style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:8 }} onClick={()=>navigate(session?"feed":"explore")}>
          {/* El nombre ya lo pinta <Logo>; antes iba aparte porque el PNG
              llevaba el texto incrustado e ilegible a este tamaño. */}
          <Logo height={isMobile?30:38} />
        </div>

        {/* En móvil las secciones se mueven al desplegable: en una pantalla de
            ~390px no caben logo + 4 secciones + los iconos de sesión. */}
        {!isMobile&&(
          <div style={{ display:"flex", gap:4 }}>
            {NAV.map(n=>{
              const active = n.id==="miperfil" ? location.pathname.startsWith("/profile") : location.pathname===pageToPath(n.id);
              return <button key={n.id} style={S.navLink(active)} onClick={()=>n.id==="miperfil"?navigate("profile",session?.user?.id):navigate(n.id)}>{n.label}</button>;
            })}
            {session&&isAdmin(session?.user,profile)&&<button style={S.navLink(location.pathname==="/admin")} onClick={()=>navigate("admin")}>Admin</button>}
          </div>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:8, position:"relative" }}>
          {/* En desktop, como texto igual que Ajustes. En móvil no cabe el
              texto, así que va como icono junto al emoticono de perfil: es
              donde se espera encontrarlo, no enterrado en el desplegable. */}
          {!isMobile&&session&&(<>
            <button
              style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(0,0,0,0.55)", fontSize:13, fontFamily:"'Geist',sans-serif", fontWeight:600, display:"flex", alignItems:"center", gap:6 }}
              onClick={()=>{ setShowNotifs(!showNotifs); if(!showNotifs) setUnreadCount(0); }}>
              Notificaciones
              {unreadCount>0&&<span style={{ background:"#e11d48", color:"#fff", borderRadius:9, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, padding:"0 5px" }}>{unreadCount}</span>}
            </button>
            {showNotifs&&<Suspense fallback={null}><NotificationsPanel userId={session.user.id} onClose={()=>setShowNotifs(false)} onNavigate={navigate} /></Suspense>}
          </>)}

          {isMobile ? (
            <>
              {session && (<>
                <button
                  aria-label="Notificaciones"
                  onClick={()=>{ setShowNotifs(!showNotifs); if(!showNotifs) setUnreadCount(0); }}
                  style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 8px", fontSize:20, position:"relative" }}
                  title="Notificaciones">
                  🔔
                  {unreadCount>0&&<span style={{ position:"absolute", top:2, right:0, background:"#e11d48", color:"#fff", borderRadius:9, minWidth:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, padding:"0 4px" }}>{unreadCount}</span>}
                </button>
                {showNotifs&&<Suspense fallback={null}><NotificationsPanel userId={session.user.id} onClose={()=>setShowNotifs(false)} onNavigate={navigate} /></Suspense>}
              </>)}
              {session && (
                <button
                  aria-label="Mi Perfil"
                  onClick={()=>navigate("profile",session?.user?.id)}
                  style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 8px", fontSize:20 }}
                  title="Mi Perfil">
                  👤
                </button>
              )}
              <button
                aria-label="Menú"
                aria-expanded={menuOpen}
                onClick={()=>setMenuOpen(o=>!o)}
                style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 4px", display:"flex", flexDirection:"column", justifyContent:"center", gap:4, width:28 }}>
                {[0,1,2].map(i=>(
                  <span key={i} style={{ display:"block", height:2, width:20, background:"#1a1a1a", borderRadius:2 }} />
                ))}
              </button>
            </>
          ) : session ? (<>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(0,0,0,0.55)", fontSize:12, fontFamily:"'Geist',sans-serif", fontWeight:700, letterSpacing:1 }} onClick={toggleLang} title="Idioma">{lang==="es"?"EN":"ES"}</button>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(0,0,0,0.55)", fontSize:13, fontFamily:"'Geist',sans-serif", fontWeight:600 }} onClick={()=>navigate("settings")} title="Ajustes">Ajustes</button>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(0,0,0,0.55)", fontSize:13, fontFamily:"'Geist',sans-serif", fontWeight:600 }} onClick={signOut}>{t(lang,"logout")}</button>
          </>) : (
            <button style={{ background:"#1a1a1a", border:"1.5px solid #1a1a1a", cursor:"pointer", color:"#fff", padding:"7px 16px", borderRadius:6, fontSize:13, fontFamily:"'Geist',sans-serif", fontWeight:600 }} onClick={()=>setGuestMode(false)}>Entrar</button>
          )}
        </div>

        {isMobile&&menuOpen&&(
          <div style={{ position:"absolute", top:52, left:0, right:0, background:"#fff", borderBottom:"1px solid #eee", boxShadow:"0 8px 24px rgba(0,0,0,0.06)", padding:"8px 12px 12px", display:"flex", flexDirection:"column", gap:2, zIndex:200 }}>
            {NAV.map(n=>{
              const active = n.id==="miperfil" ? location.pathname.startsWith("/profile") : location.pathname===pageToPath(n.id);
              return <button key={n.id} style={{ ...S.navLink(active), textAlign:"left", padding:"11px 8px", fontSize:15 }} onClick={()=>n.id==="miperfil"?navigate("profile",session?.user?.id):navigate(n.id)}>{n.label}</button>;
            })}
            {session&&isAdmin(session?.user,profile)&&<button style={{ ...S.navLink(location.pathname==="/admin"), textAlign:"left", padding:"11px 8px", fontSize:15 }} onClick={()=>navigate("admin")}>Admin</button>}

            <div style={{ borderTop:"1px solid #eee", marginTop:6, paddingTop:6, display:"flex", flexDirection:"column", gap:2 }}>
              {session ? (<>
                <button style={{ ...S.navLink(location.pathname==="/settings"), textAlign:"left", padding:"11px 8px", fontSize:15 }} onClick={()=>navigate("settings")}>Ajustes</button>
                <button style={{ ...S.navLink(false), textAlign:"left", padding:"11px 8px", fontSize:15 }} onClick={()=>{ toggleLang(); setMenuOpen(false); }}>{lang==="es"?"English":"Español"}</button>
                <button style={{ ...S.navLink(false), textAlign:"left", padding:"11px 8px", fontSize:15 }} onClick={()=>{ setMenuOpen(false); signOut(); }}>{t(lang,"logout")}</button>
              </>) : (
                <button style={{ ...S.btn("primary"), background:"#1a1a1a", color:"#fff", width:"100%", marginTop:4 }} onClick={()=>{ setMenuOpen(false); setGuestMode(false); }}>Entrar</button>
              )}
            </div>
          </div>
        )}
      </nav>
      <main style={S.main}>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={session ? <FeedPage user={session.user} onNavigate={navigate} /> : <ExplorePage onNavigate={navigate} currentUser={null} />} />
          <Route path="/explore" element={<ExplorePage onNavigate={navigate} currentUser={currentUser} />} />
          <Route path="/relojes" element={<RelojesPage onNavigate={navigate} currentUser={currentUser} />} />
          <Route path="/foros" element={<ForosPage currentUser={currentUser} onNavigate={navigate} onLoginRequired={()=>setGuestMode(false)} />} />
          <Route path="/watch/:slug" element={<WatchRoute currentUser={currentUser} onNavigate={navigate} onLoginRequired={()=>setGuestMode(false)} />} />
          <Route path="/brand/:brandSlug" element={<BrandRoute currentUser={currentUser} onNavigate={navigate} />} />
          <Route path="/thread/:threadId" element={<ThreadRoute currentUser={currentUser} onNavigate={navigate} onLoginRequired={()=>setGuestMode(false)} />} />
          <Route path="/profile/:userId" element={<ProfileRoute currentUser={currentUser||{id:""}} onNavigate={navigate} />} />
          <Route path="/settings" element={session ? <SettingsPage user={session.user} onSaved={()=>{ loadProfile(session.user.id); navigate("profile",session.user.id); }} /> : <Navigate to="/" replace />} />
          <Route path="/garage" element={session ? <GaragePage currentUser={session.user} onNavigate={navigate} lang={lang} /> : <Navigate to="/" replace />} />
          <Route path="/mantenimiento" element={session ? <MantenimientoPage currentUser={session.user} onNavigate={navigate} lang={lang} /> : <Navigate to="/" replace />} />
          <Route path="/garage/:watchId" element={session ? <GarageWatchRoute currentUser={session.user} onNavigate={navigate} /> : <Navigate to="/" replace />} />
          <Route path="/wristcheck" element={<WristCheckPage currentUser={currentUser} onNavigate={navigate} />} />
          <Route path="/listas" element={<ListasPage currentUser={currentUser} onNavigate={navigate} />} />
          <Route path="/talleres" element={<WorkshopsPage currentUser={currentUser} onNavigate={navigate} />} />
          <Route path="/ranking" element={<RankingPage currentUser={currentUser} onNavigate={navigate} />} />
          <Route path="/create-watch" element={session ? <CreateWatchPage currentUser={session.user} onNavigate={navigate} /> : <Navigate to="/" replace />} />
          <Route path="/admin" element={<AdminPage user={session?.user} profile={profile} onNavigate={navigate} />} />
          <Route path="/privacidad" element={<PrivacyPage onNavigate={navigate} />} />
          <Route path="/terminos" element={<TermsPage onNavigate={navigate} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      {session&&<FeedbackButton userId={session.user.id} />}
      <Footer onNavigate={navigate} isGuest={!session} />
    </div>
  );
}
